/**
 * Bibliothèque technique : domaines, lots, sous-lots et ouvrages. Chaque ouvrage pointe vers une famille
 * (ses variables et ses formules) et une de ses sorties : il n'y a aucune formule en double.
 * Les ouvrages sans géométrie propre (forfaits, équipements) passent par les familles génériques.
 */
export interface LibraryItem {
  code: string;
  designation: string;
  family: string;
  output: string;
  /** Lot du quantitatif où la ligne sera placée. */
  chapter: string;
  /** Unité affichée si elle diffère de la sortie (forfait). */
  unit?: string;
}

export interface LibraryLot {
  code: string;
  name: string;
  items?: LibraryItem[];
  sub?: LibraryLot[];
}

export interface LibraryDomain {
  code: string;
  name: string;
  description: string;
  /** Types de projets couverts. */
  typologies: string[];
  lots: LibraryLot[];
}

type Ref = [family: string, output: string];
const U: Ref = ['OUVRAGE_U', 'nombre'];
const ML: Ref = ['OUVRAGE_ML', 'longueur'];
const M2: Ref = ['OUVRAGE_M2', 'surface'];
const M3: Ref = ['OUVRAGE_M3', 'volume'];
const BA: Ref = ['BLOC_BA', 'volume'];
const ACIER: Ref = ['ACIERS_RATIO', 'masse'];
const FOUILLE: Ref = ['FOUILLE_OUVRAGE', 'volume'];
const REMBLAI: Ref = ['REMBLAI_FOUILLE', 'volume'];
const DISQUE: Ref = ['DISQUE_BA', 'volume'];
const PAROI: Ref = ['PAROI_CYLINDRIQUE', 'volume'];
const METAL: Ref = ['MASSE_METALLIQUE', 'masse'];
const POTEAU: Ref = ['POTEAU', 'volume'];
const MUR: Ref = ['MUR_MACONNERIE', 'surface'];
const DALLE: Ref = ['DALLE_PLEINE', 'volume'];

const lot = (prefix: string, chapter: string, code: string, name: string, items: Array<[key: string, designation: string, ref: Ref, unit?: string]>): LibraryLot => ({
  code,
  name,
  items: items.map(([key, designation, [family, output], unit]) => ({ code: `${prefix}.${code}.${key}`, designation, family, output, chapter, ...(unit ? { unit } : {}) })),
});

export const SYSTEM_LIBRARY: readonly LibraryDomain[] = [
  {
    code: 'BATIMENT',
    name: 'Bâtiment',
    description: 'Du gros œuvre aux finitions, pour tous les types de bâtiments.',
    typologies: ['Maisons individuelles', 'Villas', 'Immeubles', 'Bâtiments administratifs', 'Bâtiments commerciaux', 'Bâtiments industriels', 'Bâtiments scolaires', 'Bâtiments sanitaires', 'Réhabilitation', 'Rénovation'],
    lots: [
      lot('BAT', 'PREP', 'INST', 'Installation de chantier', [['AMENE', 'Amenée et repli du matériel', U, 'FF'], ['CLOTURE', 'Clôture de chantier', ML], ['PANNEAU', 'Panneau de chantier', U], ['IMPLANT', 'Implantation', U, 'FF']]),
      lot('BAT', 'TERR', 'TERR', 'Terrassement', [['DEC', 'Décapage de la terre végétale', ['DECAPAGE', 'surface']], ['MASSE', 'Fouille en pleine masse', ['FOUILLE_MASSE', 'volume']], ['RIGOLE', 'Fouille en rigole', ['FOUILLE_RIGOLE', 'volume']], ['SURF', 'Terrassement sur surface', ['TERRASSEMENT_SURFACE', 'volume']], ['TERMITE', 'Traitement anti-termites', ['TRAITEMENT_TERMITE', 'surface']]]),
      {
        code: 'FOND',
        name: 'Fondations',
        sub: [
          lot('BAT.FOND', 'FOND', 'BP', 'Béton de propreté', [['SI', 'Propreté sous semelle isolée', ['SEMELLE_ISOLEE', 'proprete']], ['SF', 'Propreté sous semelle filante', ['SEMELLE_FILANTE', 'proprete']]]),
          lot('BAT.FOND', 'FOND', 'SEM', 'Semelles et radiers', [['SI', 'Semelle isolée', ['SEMELLE_ISOLEE', 'volume']], ['SF', 'Semelle filante', ['SEMELLE_FILANTE', 'volume']], ['RAD', 'Radier', ['RADIER', 'volume']], ['LON', 'Longrine', ['LONGRINE', 'volume']], ['HER', 'Hérissonnage', ['HERISSONNAGE', 'volume']]]),
        ],
      },
      {
        code: 'BA',
        name: 'Béton armé',
        sub: [
          lot('BAT.BA', 'BA', 'POT', 'Poteaux', [['POT', 'Poteau en béton armé', POTEAU]]),
          lot('BAT.BA', 'BA', 'POU', 'Poutres et chaînages', [['POU', 'Poutre', ['POUTRE', 'volume']], ['CHA', 'Chaînage', ['CHAINAGE', 'volume']], ['ACR', 'Acrotère', ['ACROTERE', 'volume']]]),
          lot('BAT.BA', 'BA', 'DAL', 'Dalles', [['DAL', 'Dalle pleine', DALLE], ['HOU', 'Plancher à hourdis', ['PLANCHER_HOURDIS', 'surface']], ['DAG', 'Dallage', ['DALLAGE', 'volume']]]),
          lot('BAT.BA', 'BA', 'ESC', 'Escaliers et voiles', [['ESC', 'Escalier', ['ESCALIER', 'volume']], ['VOI', 'Voile', ['VOILE', 'volume']]]),
        ],
      },
      lot('BAT', 'MAC', 'MAC', 'Maçonnerie et élévation', [['MUR', 'Mur en maçonnerie', MUR]]),
      lot('BAT', 'ENDUIT', 'END', 'Enduits', [['MUR', 'Enduit sur mur', ['ENDUIT_MUR', 'surface']], ['PLA', 'Enduit de plafond', ['ENDUIT_PLAFOND', 'surface']]]),
      lot('BAT', 'ETANCH', 'ETA', 'Étanchéité', [['TER', 'Étanchéité de terrasse', ['ETANCHEITE_TERRASSE', 'surface']], ['PRO', "Protection d'étanchéité", ['PROTECTION_ETANCHEITE', 'surface']], ['PEN', 'Forme de pente', ['FORME_PENTE', 'volume']], ['BAD', 'Badigeon de soubassement', ['BADIGEON_SOUBASSEMENT', 'surface']]]),
      lot('BAT', 'MENUIS_MET', 'MEN', 'Menuiserie', [['BOIS', 'Menuiserie bois', ['MENUISERIE_BOIS', 'nombre']], ['MET', 'Menuiserie métallique', ['MENUISERIE_METAL', 'nombre']], ['ALU', 'Menuiserie aluminium', ['MENUISERIE_ALU', 'nombre']]]),
      lot('BAT', 'ELEC', 'ELE', 'Électricité', [['PT', 'Point électrique', ['POINT_ELECTRIQUE', 'nombre']], ['CAB', 'Câble électrique', ['CABLE_ELECTRIQUE', 'longueur']]]),
      lot('BAT', 'PLOMB', 'PLO', 'Plomberie', [['APP', 'Appareil sanitaire', ['APPAREIL_SANITAIRE', 'nombre']], ['CAN', 'Canalisation', ['CANALISATION', 'longueur']]]),
      lot('BAT', 'REVET', 'CAR', 'Carrelage', [['SOL', 'Carrelage de sol', ['CARRELAGE_SOL', 'surface']], ['FAI', 'Faïence', ['FAIENCE', 'surface']], ['CHA', 'Chape', ['CHAPE', 'surface']]]),
      lot('BAT', 'PEINT', 'PEI', 'Peinture', [['MUR', 'Peinture sur mur', ['PEINTURE_MUR', 'surface']], ['SUR', 'Peinture sur surface', ['PEINTURE_SURFACE', 'surface']]]),
      lot('BAT', 'CHARP', 'CHA', 'Charpente', [['CHA', 'Charpente', ['CHARPENTE', 'surface']]]),
      lot('BAT', 'COUV', 'COU', 'Couverture', [['COU', 'Couverture', ['COUVERTURE', 'surface']], ['GOU', 'Gouttière', ['GOUTTIERE', 'longueur']], ['DES', "Descente d'eaux pluviales", ['DESCENTE_EP', 'longueur']]]),
      lot('BAT', 'AUTRES', 'FIN', 'Finitions', [['FP', 'Faux plafond', ['FAUX_PLAFOND', 'surface']], ['DIV', 'Finition au mètre carré', M2]]),
    ],
  },
  {
    code: 'CLOTURE',
    name: 'Murs de clôture',
    description: 'Clôtures maçonnées avec calculateur complet.',
    typologies: ['Clôtures de parcelle', 'Clôtures de site', 'Murs avec portail'],
    lots: [
      lot('CLO', 'PREP', 'IMP', 'Implantation', [['IMP', 'Implantation du mur', ML]]),
      lot('CLO', 'TERR', 'DEC', 'Décapage', [['DEC', 'Décapage', ['DEBROUSSAILLAGE', 'surface']]]),
      lot('CLO', 'TERR', 'FOU', 'Fouilles', [['FOU', 'Fouilles en rigole', FOUILLE], ['REM', 'Remblai des fouilles', REMBLAI]]),
      lot('CLO', 'FOND', 'FON', 'Béton de propreté, semelles, longrines', [['BP', 'Béton de propreté', ['SEMELLE_FILANTE', 'proprete']], ['SEM', 'Semelle', ['SEMELLE_FILANTE', 'volume']], ['LON', 'Longrine', ['LONGRINE', 'volume']]]),
      lot('CLO', 'BA', 'POT', 'Poteaux en béton armé', [['POT', 'Poteaux', POTEAU]]),
      lot('CLO', 'MAC', 'MAC', 'Maçonnerie en agglos', [['MUR', 'Maçonnerie en agglos', MUR]]),
      lot('CLO', 'BA', 'CHA', 'Chaînages', [['CHA', 'Chaînages', ['CHAINAGE', 'volume']]]),
      lot('CLO', 'ENDUIT', 'END', 'Enduits', [['END', 'Enduits', ['ENDUIT_MUR', 'surface']]]),
      lot('CLO', 'PEINT', 'PEI', 'Peinture', [['PEI', 'Peinture', ['PEINTURE_MUR', 'surface']]]),
      lot('CLO', 'MENUIS_MET', 'PRT', 'Portails et grilles métalliques', [['POR', 'Portail', U], ['GRI', 'Grilles métalliques', ML]]),
      lot('CLO', 'MAC', 'COU', 'Couronnement', [['COU', 'Couronnement', ML]]),
    ],
  },
  {
    code: 'ROUTES',
    name: 'Routes et travaux publics',
    description: 'Terrassements, chaussée, dépendances, signalisation et assainissement routier.',
    typologies: ['Routes', 'Voiries urbaines', 'Pistes', 'Parkings et plateformes'],
    lots: [
      lot('RTE', 'PREP', 'INST', 'Études et installation de chantier', [['ETU', "Études d'exécution", U, 'FF'], ['INS', 'Installation de chantier', U, 'FF']]),
      lot('RTE', 'TERR', 'PREP', 'Débroussaillage et décapage', [['DEB', 'Débroussaillage', ['DEBROUSSAILLAGE', 'surface']], ['DEC', 'Décapage', ['DEBROUSSAILLAGE', 'surface']]]),
      lot('RTE', 'TERR', 'TERR', 'Terrassements', [['DBL', 'Déblais', ['TERRASSEMENT_LINEAIRE', 'volume']], ['RBL', 'Remblais', ['TERRASSEMENT_LINEAIRE', 'volume']], ['SUR', 'Terrassement sur surface', ['TERRASSEMENT_SURFACE', 'volume']], ['ENG', "Durée d'engin au rendement (indicative)", ['ENGIN_RENDEMENT', 'duree']]]),
      lot('RTE', 'ROUTE', 'TRA', 'Transport des matériaux', [['TRA', 'Transport des matériaux', ['TRANSPORT_MATERIAUX', 'transport']]]),
      {
        code: 'CHA',
        name: 'Corps de chaussée',
        sub: [
          lot('RTE.CHA', 'ROUTE', 'GRA', 'Couches en grave', [['CF', 'Couche de forme', ['COUCHE_CHAUSSEE', 'volume']], ['FD', 'Couche de fondation', ['COUCHE_CHAUSSEE', 'volume']], ['BS', 'Couche de base', ['COUCHE_CHAUSSEE', 'volume']], ['GNT', 'Grave non traitée', ['COUCHE_CHAUSSEE', 'volume']]]),
          lot('RTE.CHA', 'ROUTE', 'ENR', 'Enrobés et revêtements', [['GB', 'Grave-bitume', ['COUCHE_ENROBE', 'masse']], ['BB', 'Béton bitumineux', ['COUCHE_ENROBE', 'masse']], ['ES', 'Enduit superficiel', ['ENDUIT_SUPERFICIEL', 'surface']]]),
        ],
      },
      lot('RTE', 'ROUTE', 'DEP', 'Accotements, bordures et caniveaux', [['ACC', 'Accotements', ['ACCOTEMENT', 'volume']], ['BOR', 'Bordures', ML], ['CAN', 'Caniveau en béton', ['CANIVEAU', 'volume']]]),
      lot('RTE', 'SIGNAL', 'SIG', 'Signalisation et sécurité', [['HOR', 'Signalisation horizontale', ['MARQUAGE_ROUTE', 'surface']], ['VER', 'Signalisation verticale', U], ['SEC', 'Équipements de sécurité', ML]]),
      lot('RTE', 'ROUTE', 'ASS', 'Assainissement routier', [['DAL', 'Ouvrage de franchissement courant', U], ['FOS', 'Fossé', ['TERRASSEMENT_LINEAIRE', 'volume']], ['COL', 'Collecteur', ML]]),
    ],
  },
  {
    code: 'OUVRAGES_ART',
    name: "Ouvrages d'art",
    description: 'Ponts, dalots, buses, culées, piles, tabliers et murs de soutènement.',
    typologies: ['Ponts en béton armé', 'Ponts métalliques', 'Dalots simples', 'Dalots doubles', 'Dalots multiples', 'Buses', 'Ouvrages de franchissement', 'Murs de soutènement'],
    lots: [
      lot('OA', 'OA', 'DAL', 'Dalots', [['FOU', 'Terrassements', ['TERRASSEMENT_SURFACE', 'volume']], ['BP', 'Béton de propreté', M3], ['RAD', 'Radier inférieur', BA], ['PIE', 'Piédroits', BA], ['DAL', 'Dalle supérieure', BA], ['ACI', 'Aciers', ACIER], ['COF', 'Coffrage', M2], ['ETA', 'Étanchéité', M2], ['REM', 'Remblai', REMBLAI], ['PRO', 'Protection et aménagements', U]]),
      lot('OA', 'OA', 'BUS', 'Buses', [['BUS', 'Buses', ML], ['LIT', 'Lit de pose', M3], ['TET', 'Têtes de buses', U]]),
      lot('OA', 'OA', 'PON', 'Ponts en béton armé', [['FON', 'Fondations', BA], ['CUL', 'Culées', BA], ['PIL', 'Piles', BA], ['TAB', 'Tablier', BA], ['ACI', 'Aciers', ACIER]]),
      lot('OA', 'OA', 'PME', 'Ponts métalliques', [['CHA', 'Charpente métallique', METAL], ['ANT', 'Protection anticorrosion', M2]]),
      lot('OA', 'OA', 'SOU', 'Murs de soutènement', [['SEM', 'Semelle', BA], ['VOI', 'Voile', BA], ['ACI', 'Aciers', ACIER], ['DRA', 'Drainage', ML]]),
    ],
  },
  {
    code: 'HYDRAULIQUE',
    name: 'Ouvrages hydrauliques',
    description: "Châteaux d'eau, réservoirs, bassins, stations de pompage et réseaux d'évacuation.",
    typologies: ["Châteaux d'eau en béton armé", "Châteaux d'eau métalliques", 'Réservoirs', 'Bassins', 'Stations de pompage', "Réseaux d'évacuation"],
    lots: [
      lot('HYD', 'HYDRAU', 'CBA', "Châteaux d'eau en béton armé", [['FON', 'Fondations', FOUILLE], ['RAD', 'Radier', DISQUE], ['FUT', 'Fût', PAROI], ['CUV', 'Cuve', PAROI], ['COU', 'Coupole', ['COUPOLE', 'volume']], ['POU', 'Poutres et éléments porteurs', BA], ['ECH', 'Escaliers et échelles', ML], ['TUY', 'Tuyauterie', ML], ['ACC', 'Accessoires', U], ['PEI', 'Peinture', M2], ['ACI', 'Aciers', ACIER]]),
      lot('HYD', 'HYDRAU', 'CME', "Châteaux d'eau métalliques", [['MAS', 'Massifs de fondation', BA], ['SUP', 'Supports', METAL], ['CUV', 'Cuve', METAL], ['ECH', 'Escaliers et échelles', ML], ['TUY', 'Tuyauterie', ML], ['ACC', 'Accessoires', U], ['ANT', 'Peinture et protection anticorrosion', M2]]),
      lot('HYD', 'HYDRAU', 'RES', 'Réservoirs et bassins', [['RAD', 'Radier circulaire', DISQUE], ['PAR', 'Paroi cylindrique', PAROI], ['RAR', 'Radier rectangulaire', BA], ['VOI', 'Parois rectangulaires', BA], ['ETA', 'Étanchéité', M2]]),
      lot('HYD', 'HYDRAU', 'POM', 'Stations de pompage', [['GEN', 'Génie civil', BA], ['EQU', 'Équipements de pompage', U]]),
      lot('HYD', 'HYDRAU', 'RES2', "Réseaux d'évacuation", [['CAN', 'Caniveaux', ['CANIVEAU', 'volume']], ['COL', 'Collecteurs', ML], ['REG', 'Regards', ['REGARD', 'nombre']], ['CHV', 'Chambres de visite', U]]),
    ],
  },
  {
    code: 'AUTRES',
    name: 'Autres ouvrages',
    description: 'Aménagements urbains, réseaux divers, génie rural, ouvrages industriels, démolition. À enrichir par votre entreprise.',
    typologies: ['Aménagements urbains', 'Réseaux divers', 'Travaux agricoles', 'Génie rural', 'Travaux hydrauliques', 'Ouvrages industriels', 'Travaux de démolition'],
    lots: [
      lot('AUT', 'AUTRES', 'GEN', 'Ouvrages génériques', [['U', "Ouvrage à l'unité", U], ['ML', 'Ouvrage au mètre linéaire', ML], ['M2', 'Ouvrage au mètre carré', M2], ['M3', 'Ouvrage au mètre cube', M3], ['BA', 'Élément en béton', BA]]),
      lot('AUT', 'AUTRES', 'DEM', 'Démolition', [['M3', 'Démolition au mètre cube', M3], ['M2', 'Démolition au mètre carré', M2]]),
    ],
  },
];

/** Ouvrages d'un domaine, sous-lots compris, avec leur chemin. */
export function domainItems(domain: LibraryDomain): Array<LibraryItem & { lotPath: string[] }> {
  const walk = (lots: LibraryLot[], path: string[]): Array<LibraryItem & { lotPath: string[] }> =>
    lots.flatMap((l) => [...(l.items ?? []).map((i) => ({ ...i, lotPath: [...path, l.name] })), ...walk(l.sub ?? [], [...path, l.name])]);
  return walk(domain.lots, []);
}
